/// <amd-module name="Wit/Common/WitSettingsViews/Components/CriteriaTable/CriteriaTableModel" />
import {
    IObservableArray,
    IObservableValue,
    IReadyableObservableArray,
    ObservableArray,
    ObservableValue,
    ReadyableObservableArray
} from "VSS/Core/Observable";
import { localeIgnoreCaseComparer, newGuid } from "VSS/Core/Util/String";
import { IListBoxItem } from "VSSUI/ListBox";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { FieldType } from "Wit/Common/Generated/Constants";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import { getLocalizedOperator } from "Wit/Common/WiqlOperators";
import * as Resources from "Wit/Common/WitSettingsViews/Resources";
import { IWorkItemFieldService } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService";
import { IWorkItemFieldService2 } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService2";
import { getListBoxItemsFromFields, getSupportedFieldDefinitions } from "../../Utils/FieldUtils";
import { CriteriaConstants } from "./CriteriaConstants";
import { ICriteriaSelectedSettingData, ICriteriaSettingData } from "./CriteriaInterfaces";
import { areCriteriaEqual } from "./CriteriaService";

export interface ICriteriaTableModel {
    /**
     * Indicates if fields have been loaded yet
     */
    fieldsReady: IObservableValue<boolean>;
    isDirty: IObservableValue<boolean>;
    isValid: IObservableValue<boolean>;
    dataProviderErrorMessage: IObservableValue<string | undefined>;
    validationErrorMessage: IObservableValue<string | undefined>;
    /**
     * Action to take when a row is added
     */
    addRow: () => Promise<void>;
    /**
     * Action to take when a row is deleted
     */
    deleteRow: (rowIndex: number) => Promise<void>;
    /**
     * Action to take when a row's field is updated
     */
    updateField: (rowIndex: number, fieldReferenceName: string) => Promise<void>;
    /**
     * Action to take when a row's operator is updated
     */
    updateOperator: (rowIndex: number, operator: string) => Promise<void>;
    /**
     * Action to take when a row's value is updated
     */
    updateValue: (rowIndex: number, value: string) => Promise<void>;
    /**
     * Row data for all configured field criteria
     */
    rows: IObservableArray<ICriteriaSettingData>;
}

export class CriteriaTableModel implements ICriteriaTableModel {
    private readonly _dataProviderErrorMessage: IObservableValue<string | undefined>;
    private readonly _validationErrorMessage: IObservableValue<string | undefined>;
    public get fieldsReady(): IObservableValue<boolean> {
        return this._fieldListBoxItems.ready;
    }
    public get dataProviderErrorMessage(): IObservableValue<string | undefined> {
        return this._dataProviderErrorMessage;
    }
    public get validationErrorMessage(): IObservableValue<string | undefined> {
        return this._validationErrorMessage;
    }

    public get isDirty(): IObservableValue<boolean> {
        return this._isDirty;
    }

    public get isValid(): IObservableValue<boolean> {
        return this._isValid;
    }

    public get rows(): IObservableArray<ICriteriaSettingData> {
        return this._rows;
    }
    private _isDirty: IObservableValue<boolean>;
    private _isValid: IObservableValue<boolean>;
    private _rows: IObservableArray<ICriteriaSettingData>;
    private _initialized: Promise<void>;
    private _defaultField: IFieldEntry;
    private _fieldListBoxItems: IReadyableObservableArray<IListBoxItem>;
    private _operators: { [fieldReferenceName: string]: string[] };
    private _originalCriteria: ICriteriaSelectedSettingData[] | undefined;

    public constructor(
        private _fieldService: IWorkItemFieldService | IWorkItemFieldService2,
        notSupportedFieldReferenceNames: Set<string>,
        supportedFieldTypes: Set<FieldType>,
        initialCriteria?: ICriteriaSelectedSettingData[],
        private onCriteriaChanged?: (criteria: ICriteriaSelectedSettingData[]) => void,
        private onError?: (error: string | undefined) => void
    ) {
        this._isDirty = new ObservableValue<boolean>(false);
        this._isValid = new ObservableValue<boolean>(true);
        this._dataProviderErrorMessage = new ObservableValue<string | undefined>(undefined);
        this._validationErrorMessage = new ObservableValue<string | undefined>(undefined);
        this._rows = new ObservableArray([]);

        this._fieldListBoxItems = new ReadyableObservableArray([]);
        this._operators = {};

        this._initialized = this._initialize(notSupportedFieldReferenceNames, supportedFieldTypes, initialCriteria);
    }

    public async addRow(criteriaSetting?: ICriteriaSelectedSettingData): Promise<void> {
        await this._initialized;
        const newRow = this._getDefaultCriteria(criteriaSetting);
        this._rows.push(newRow);
        const index = this._rows.length - 1;

        await this._updateField(index, newRow.field, !!criteriaSetting);
    }

    private async _setInitialCriteria(criteriaSettings: ICriteriaSelectedSettingData[]): Promise<void> {
        // save criteria for dirty comparisons
        this._originalCriteria = criteriaSettings;
        this.clear();
        criteriaSettings.forEach(criteria => this.addRow(criteria));
    }

    private _removeCriteriaRow(rowIndex: number): void {
        const rows = this._rows.value;
        rows.splice(rowIndex, 1);
        this._rows.value = rows;

        // removing a row can't put model into an error state
        this._validateCriteria();
        this.onCriteriaChanged?.(this.rows.value);
    }

    public async updateField(rowIndex: number, fieldReferenceName: string): Promise<void> {
        await this._initialized;
        if (!fieldReferenceName) {
            return;
        }
        await this._updateField(rowIndex, fieldReferenceName);
    }

    public async updateOperator(rowIndex: number, operator: string): Promise<void> {
        await this._initialized;
        this._updateOperator(rowIndex, operator);
    }

    public async updateValue(rowIndex: number, value: string): Promise<void> {
        await this._initialized;
        this._updateValue(rowIndex, value);
    }

    public async deleteRow(rowIndex: number): Promise<void> {
        await this._initialized;
        this._removeCriteriaRow(rowIndex);
    }

    private _updateOperator(index: number, operator: string, skipValidation?: boolean, skipChangedNotification?: boolean): void {
        const criteriaSettingsData = this._rows.value[index];
        criteriaSettingsData.operator = operator;
        !skipValidation && this._validateCriteria();
        !skipChangedNotification && this.onCriteriaChanged?.(this.rows.value);
    }

    private _updateValue(index: number, value: string, skipValidation?: boolean, skipChangedNotification?: boolean): void {
        const criteriaSettingsData = this._rows.value[index];
        criteriaSettingsData.criteriaValue.value = value;
        !skipValidation && this._validateCriteria();
        !skipChangedNotification && this.onCriteriaChanged?.(this.rows.value);
    }

    private async _initialize(
        notSupportedFieldReferenceNames: Set<string>,
        supportedFieldTypes: Set<FieldType>,
        initialCriteria?: ICriteriaSelectedSettingData[]
    ): Promise<void> {
        const fields = (this._fieldService as any).loadFields 
                    ? await (this._fieldService as IWorkItemFieldService2).loadFields()
                    : await (this._fieldService as IWorkItemFieldService).getFieldsAsync();

        if (fields) {
            const supportedFields = getSupportedFieldDefinitions(fields, supportedFieldTypes, notSupportedFieldReferenceNames);
            this._fieldListBoxItems.value = getListBoxItemsFromFields(supportedFields);
            this._fieldListBoxItems.ready.value = true;
            this._defaultField = supportedFields[0];
            this._getOperators(this._defaultField);
            initialCriteria && this._setInitialCriteria(initialCriteria);
        } else {
            const errorMessage = this._fieldService.getFieldsErrorMessage();
            this._dataProviderErrorMessage.value = errorMessage;
            errorMessage && this.onError?.(errorMessage);
        }
    }

    private clear(): void {
        this._rows.value = [];
        this._isValid.value = true;
        this._isDirty.value = false;
    }

    private async _updateField(index: number, fieldReferenceName: string, preserveSelection?: boolean): Promise<void> {
        // make sure field data is loaded
        await this._initialized;
        const field = this._fieldService.getFieldByReferenceName(fieldReferenceName);
        if (!field) {
            return;
        }

        const criteriaSettingsData = this._rows.value[index];
        const fieldIndex = criteriaSettingsData.fields.value.findIndex(item => item.id === fieldReferenceName);
        criteriaSettingsData.field = fieldReferenceName;
        criteriaSettingsData.fieldSelection.select(fieldIndex);

        // clear selections
        criteriaSettingsData.operatorSelection.clear();

        // update operator options
        const operatorsForField = this._getOperators(field);
        let indexToSelect = 0;
        if (preserveSelection) {
            const operatorIndex = operatorsForField.findIndex(operator => operator === criteriaSettingsData.operator);
            indexToSelect = operatorIndex >= 0 ? operatorIndex : indexToSelect;
        }

        const operator = operatorsForField[indexToSelect];
        this._updateOperator(index, operator, /** skipValidation */ true, /** skipChangedNotification */ true);
        criteriaSettingsData.operators.value = this._getListBoxItems(operatorsForField);
        criteriaSettingsData.operatorSelection.select(indexToSelect);

        // update the value and allowed values
        !preserveSelection && this._updateValue(index, "", /** skipValidation */ true, /** skipChangedNotification */ true);
        await this._updateAllowedValues(criteriaSettingsData);

        this._validateCriteria();
        this.onCriteriaChanged?.(this.rows.value);
    }

    private _getOperators(field: IFieldEntry): string[] {
        let operators = this._operators[field.referenceName];
        if (!operators) {
            operators = this._getLocalizedSupportedWiqlOperators(field);
            this._operators[field.referenceName] = operators;
        }

        return operators;
    }

    private async _updateAllowedValues(settings: ICriteriaSettingData): Promise<void> {
        settings.criteriaValues.ready.value = false;
        const field = this._fieldService.getFieldByReferenceName(settings.field);
        if (!field) {
            return;
        }

        const allowedValues = await this._getAllowedValues(field);

        // we might not have a list of allowed values
        if (allowedValues.length > 0) {
            settings.criteriaValues.value = allowedValues;
        } else {
            settings.criteriaValues.value = [];
        }

        settings.criteriaValues.ready.value = true;
    }

    private async _getAllowedValues(field: IFieldEntry): Promise<string[]> {
        let allowedValues = await this._fieldService.getAllowedValues(field.referenceName);
        if (!allowedValues) {
            this._dataProviderErrorMessage.value = this._fieldService.getFieldAllowedValuesErrorMessage();
            return [];
        }
        if (field.isIdentity) {
            allowedValues = allowedValues.filter(value => localeIgnoreCaseComparer(value, "@me") !== 0);
        }

        return allowedValues || [];
    }

    private _validateCriteria(): void {
        const settings = this._rows.value;

        if (!this._validateUniqueCriteria(settings)) {
            this._validationErrorMessage.value = Resources.DuplicateFieldCriteriaErrorMessage;
        }
        // check that there are no empty selections
        else if (settings.findIndex(s => !s.field || !s.operator) >= 0) {
            // empty to show that the model isn't valid, but there is no error message to display
            this._validationErrorMessage.value = "";
        }
        // otherwise clear error message if necessary
        else if (this._validationErrorMessage.value !== undefined) {
            this._validationErrorMessage.value = undefined;
        }

        this.onError?.(this._validationErrorMessage.value);

        this._setIsValid();
        this._setIsDirty();
    }

    private _validateUniqueCriteria(settings: ICriteriaSettingData[]): boolean {
        const uniqueCriteriaSettings: { [fieldId: string]: string[] } = {};
        for (const setting of settings) {
            const operatorAndValue = setting.operator + setting.criteriaValue.value;
            if (!uniqueCriteriaSettings[setting.field]) {
                uniqueCriteriaSettings[setting.field] = [];
            } else if (setting.operator.length > 0 && uniqueCriteriaSettings[setting.field].indexOf(operatorAndValue) >= 0) {
                // found a duplicate
                return false;
            }

            uniqueCriteriaSettings[setting.field].push(operatorAndValue);
        }

        return true;
    }

    private _getDefaultCriteria(criteriaSetting?: ICriteriaSelectedSettingData): ICriteriaSettingData {
        const defaultSelectionIndex = 0;
        const defaultReferenceName = this._defaultField.referenceName;
        const operators = this._operators[defaultReferenceName];
        const operator = operators[defaultSelectionIndex];
        const id = newGuid();

        const criteriaSettingsData = {
            id,
            field: criteriaSetting?.field || defaultReferenceName,
            operator: criteriaSetting?.operator ? getLocalizedOperator(criteriaSetting.operator) : operator,
            criteriaValue: new ObservableValue(criteriaSetting?.criteriaValue.value || ""),
            fields: this._fieldListBoxItems,
            operators: new ObservableArray(this._getListBoxItems(operators)),
            criteriaValues: new ReadyableObservableArray([], false),
            fieldSelection: new DropdownSelection(),
            operatorSelection: new DropdownSelection(),
            hasError: new ObservableValue(false)
        };

        // update selections
        criteriaSettingsData.fieldSelection.select(defaultSelectionIndex);
        criteriaSettingsData.operatorSelection.select(defaultSelectionIndex);

        return criteriaSettingsData;
    }

    private _getListBoxItems = (options: string[]): IListBoxItem[] => options.map(option => ({ id: option, text: option } as IListBoxItem));

    /**
     * Return localized supported operators for the given field
     */
    private _getLocalizedSupportedWiqlOperators(field: IFieldEntry): string[] {
        if (field.isIdentity) {
            return CriteriaConstants.equalityOperators;
        }

        switch (field.type) {
            case FieldType.String:
                return CriteriaConstants.stringOperators;
            case FieldType.Integer:
            case FieldType.DateTime:
            case FieldType.Double:
                return CriteriaConstants.comparisonOperators;
            case FieldType.PlainText:
                return field.isTextQuerySupported ? CriteriaConstants.textWithTextSupportOperators : CriteriaConstants.textOperators;
            case FieldType.TreePath:
                return CriteriaConstants.treePathOperators;
            case FieldType.Boolean:
                return CriteriaConstants.equalityOperators;
            default:
                throw new Error("The given field is not supported");
        }
    }

    private _setIsValid(): void {
        const valid = this._validationErrorMessage.value === undefined;

        if (this._isValid.value !== valid) {
            this._isValid.value = valid;
        }
    }

    private _setIsDirty(): void {
        if (!this._originalCriteria) {
            return;
        }

        const currentCriteria = this._rows.value;
        let dirty = this._originalCriteria.length != currentCriteria.length;
        if (!dirty) {
            dirty = this._originalCriteria.findIndex((criteria, index) => !areCriteriaEqual(criteria, currentCriteria[index])) >= 0;
        }

        if (this._isDirty.value !== dirty) {
            this._isDirty.value = dirty;
        }
    }
}
